import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Mail, ArrowLeft } from 'lucide-react';
import { API_BASE, fetchCsrfCookie, getCsrfToken } from '../lib/api';
import { useTranslation } from '../i18n';
import { useThemeStore } from '../stores/useThemeStore';

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { isDark } = useThemeStore();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await fetchCsrfCookie();
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken(),
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || t.forgotPassword.error);
        setLoading(false);
        return;
      }

      setSuccess(true);
    } catch {
      setError('Connection error');
    }

    setLoading(false);
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center px-4 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-vivatis-50 via-white to-vivatis-100'
      }`}
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl ${
            isDark ? 'bg-vivatis-900/20' : 'bg-vivatis-200/40'
          }`}
        />
        <div
          className={`absolute -bottom-32 -left-32 w-[30rem] h-[30rem] rounded-full blur-3xl ${
            isDark ? 'bg-vivatis-900/15' : 'bg-vivatis-300/30'
          }`}
        />
      </div>

      {/* Card */}
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl border p-8 ${
          isDark
            ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700'
            : 'bg-white/80 backdrop-blur-sm border-gray-200'
        }`}
      >
        {/* Logo area */}
        <div className="text-center mb-8">
          <img
            src="/VIV_Logo_main.svg"
            alt="Vivatis Pharma"
            className="h-12 w-auto mx-auto dark:brightness-0 dark:invert"
          />
          <p
            className={`text-sm font-medium mt-3 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {t.forgotPassword.subtitle}
          </p>
        </div>

        {success ? (
          <div className="text-center">
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-6">
              {t.forgotPassword.success}
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-vivatis-600 hover:text-vivatis-700 transition-colors"
            >
              <ArrowLeft size={16} />
              {t.forgotPassword.backToLogin}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 break-words overflow-hidden">
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-1.5 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                {t.forgotPassword.email}
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
                />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder={t.forgotPassword.emailPlaceholder}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-vivatis-600 text-white font-medium text-sm hover:bg-vivatis-700 active:bg-vivatis-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t.forgotPassword.submitting : t.forgotPassword.submit}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className={`inline-flex items-center gap-2 text-sm transition-colors ${
                  isDark ? 'text-gray-400 hover:text-vivatis-400' : 'text-gray-500 hover:text-vivatis-600'
                }`}
              >
                <ArrowLeft size={16} />
                {t.forgotPassword.backToLogin}
              </Link>
            </div>
          </form>
        )}
      </div>

      {/* Footer */}
      <p
        className={`relative mt-8 text-xs ${
          isDark ? 'text-gray-500' : 'text-gray-400'
        }`}
      >
        {t.brand.copyright}
      </p>
    </div>
  );
};

export default ForgotPasswordPage;
